import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { FC } from 'react';
import CellValue from '@app/shared/components/cell-value/CellValue';
import {
  calculatedMultiplierRules,
  enteredPercentageViewRules,
} from '@app/shared/components/cell-value/CellValueConfigurations';
import { Alignment } from '@app/shared/enums/alignment.enum';
import TableSpacerRow from '@app/shared/components/table/TableSpacerRow';
import TableSectionValuesPerMultiple from '../../components/TableSectionValuesPerMultiple';
import TableSectionValuesPerCase from '../../components/TableSectionValuesPerCase';
import { useLocale } from '@app/core/hooks/useLocale';
import {
  getWaterfallCaseValue,
  getWaterfallCaseYearMultipleValue,
} from '@app/core/store/pwerm2-calculation-slice-selectors';
import { getCalculatedProjectValueInPercent } from '@app/shared/formatters';

interface MomAndIrrSectionProps {
  columnsCount: number;
}

export const MomAndIrrSection: FC<MomAndIrrSectionProps> = ({ columnsCount }): JSX.Element => {
  const results = useAppSelector((state) => state.pwerm2Calculation.calculatedResults);
  const { l } = useLocale();

  return (
    <>
      <tr data-testid="sponsor-mom-irr-header-row">
        <th className="table-primary__cell--header-tertiary">{l('_SponsorMoMAndIRR')}</th>
        <th colSpan={columnsCount - 1} className="table-primary__cell--header-tertiary" />
      </tr>
      <TableSectionValuesPerMultiple
        title={l('_MoM')}
        renderCell={({ caseItem, forecast, multipleId }) => (
          <CellValue
            {...calculatedMultiplierRules}
            value={getWaterfallCaseYearMultipleValue(
              results,
              caseItem.caseId,
              forecast.forecastId,
              multipleId!,
              (multiple) => multiple.institutionalMoM
            )}
            dataTestId={`mom-cell-${caseItem.caseId}-${forecast.forecastId}-${multipleId}`}
          />
        )}
      />
      <TableSectionValuesPerCase
        title={l('_CaseMoM')}
        renderSpacer={false}
        renderCell={({ caseItem }) => (
          <CellValue
            {...calculatedMultiplierRules}
            alignment={Alignment.Center}
            value={getWaterfallCaseValue(
              results,
              caseItem.caseId,
              (caseItem) => caseItem.weightedMoM
            )}
            dataTestId={`case-mom-cell-${caseItem.caseId}`}
          />
        )}
        additionalTitleClassNames="table-primary__cell--strong"
        additionalCellClassNames="table-primary__cell--strong"
      />
      <tr data-testid="case-weighted-mom-row">
        <th className="table-primary__cell--vertical-separator table-primary__cell--section-start-separator table-primary__cell--strong">
          {l('_CaseWeightedMoM')}
        </th>
        <td className="table-primary__cell--vertical-separator table-primary__cell--section-start-separator" />
        <td
          colSpan={columnsCount - 2}
          className="table-primary__cell--vertical-separator table-primary__cell--section-start-separator table-primary__cell--strong">
          <CellValue
            {...calculatedMultiplierRules}
            alignment={Alignment.Center}
            value={results.waterfall.weightedMoM}
            dataTestId="case-weighted-mom-cell"
          />
        </td>
      </tr>
      <TableSpacerRow />
      <TableSectionValuesPerMultiple
        title={l('_IRR')}
        renderCell={({ caseItem, forecast, multipleId }) => {
          const irrValue = getWaterfallCaseYearMultipleValue(
            results,
            caseItem.caseId,
            forecast.forecastId,
            multipleId!,
            (multiple) => multiple.institutionalIrr
          );

          if (irrValue) {
            return (
              <CellValue
                {...enteredPercentageViewRules}
                value={getCalculatedProjectValueInPercent(irrValue)}
                dataTestId={`irr-cell-${caseItem.caseId}-${forecast.forecastId}-${multipleId}`}
              />
            );
          }

          return <></>;
        }}
        sponsorIrrTooltipInfo={l('_IrrNumbersCanBeClickedForFurtherInformation')}
      />
      <TableSectionValuesPerCase
        title={l('_CaseIRR')}
        renderSpacer={false}
        renderCell={({ caseItem }) => {
          const caseIrr = getWaterfallCaseValue(
            results,
            caseItem.caseId,
            (caseItem) => caseItem.weightedIrr
          );

          if (caseIrr) {
            return (
              <CellValue
                {...enteredPercentageViewRules}
                alignment={Alignment.Center}
                value={getCalculatedProjectValueInPercent(caseIrr)}
                dataTestId={`case-irr-cell-${caseItem.caseId}`}
              />
            );
          }

          return <></>;
        }}
        additionalTitleClassNames="table-primary__cell--strong"
        additionalCellClassNames="table-primary__cell--strong"
      />
      <tr data-testid="case-weighted-irr-row">
        <th className="table-primary__cell--vertical-separator table-primary__cell--section-start-separator table-primary__cell--strong">
          {l('_CaseWeightedIRR')}
        </th>
        <td className="table-primary__cell--vertical-separator table-primary__cell--section-start-separator" />
        <td
          colSpan={columnsCount - 2}
          className="table-primary__cell--vertical-separator table-primary__cell--section-start-separator table-primary__cell--strong">
          {results.waterfall.weightedIrr ? (
            <CellValue
              {...enteredPercentageViewRules}
              alignment={Alignment.Center}
              value={getCalculatedProjectValueInPercent(results.waterfall.weightedIrr)}
              dataTestId="case-weighted-irr-cell"
            />
          ) : (
            <></>
          )}
        </td>
      </tr>
      <TableSpacerRow />
    </>
  );
};
